<template>
  <main class="cover-auth">
    <div class="widget-auth">
      <!-- Profil utilisateur -->
      <div class="tag-state lg rounded-50 flex align-items-center gap-4 px-4 py-4 mb-6">
        <img 
          :src="userPhoto || '/public/assets/users/300w/user2.jpg'" 
          class="rounded-100 overflow-hidden size-2xl" 
          alt="Photo de profil"
        />
        <div class="px-3">
          <h5 class="my-1">{{ userName }}</h5>
          <span class="text-sm text-grey-600">{{ userEmail }} | {{ userCompany }}</span>
        </div>
        <div class="rounded-100 overflow-hidden size-2xl ml-auto">
          <img src="/public/icons/icon-coop-finance.svg" width="100%" alt="Coop Finance" />
        </div>
      </div>

      <h2 class="font-semibold text-center my-6">
        Charger vos 3 derniers bulletins de salaire
      </h2>

      <form class="form" @submit.prevent="handleSubmit">  
        <!-- Bulletin de salaire 3 (le plus récent) -->
        <label 
          for="file-upload-bulletin3" 
          class="custom-upload my-5"
          :class="{ active: formData.bulletin3 }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-bulletin3" 
              name="file-upload-bulletin3" 
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              @change="(e) => handleBulletinUpload(e, 3)"
            >
            <div class="s4 custom-upload-img">.3</div>
            <div class="s12">
              <span class="custom-upload-text">Bulletin de salaire (3)</span> 
              <b class="color-danger">*</b><br>
              <span class="custom-upload-text-sm">
                {{ formData.bulletin3 ? formData.bulletin3.name : 'Charger le doc' }}
              </span>
            </div>
            <div class="s4 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>

        <!-- Bulletin de salaire 2 -->
        <label 
          for="file-upload-bulletin2" 
          class="custom-upload my-5"
          :class="{ active: formData.bulletin2 }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-bulletin2" 
              name="file-upload-bulletin2" 
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              @change="(e) => handleBulletinUpload(e, 2)"
            >
            <div class="s4 custom-upload-img">.2</div>
            <div class="s12">
              <span class="custom-upload-text">Bulletin de salaire (2)</span> 
              <b class="color-danger">*</b><br>
              <span class="custom-upload-text-sm">
                {{ formData.bulletin2 ? formData.bulletin2.name : 'Charger le doc' }}
              </span>
            </div>
            <div class="s4 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>

        <!-- Bulletin de salaire 1 (le plus ancien) -->
        <label 
          for="file-upload-bulletin1" 
          class="custom-upload my-5"
          :class="{ active: formData.bulletin1 }"
        >
          <div class="custom-upload-st2">
            <input 
              id="file-upload-bulletin1" 
              name="file-upload-bulletin1" 
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              @change="(e) => handleBulletinUpload(e, 1)"
            >
            <div class="s4 custom-upload-img">.1</div>
            <div class="s12">
              <span class="custom-upload-text">Bulletin de salaire (1)</span> 
              <b class="color-danger">*</b><br>
              <span class="custom-upload-text-sm">
                {{ formData.bulletin1 ? formData.bulletin1.name : 'Charger le doc' }}
              </span>
            </div>
            <div class="s4 right">
              <img src="/public/icons/icon-upload.svg" class="custom-upload-icon" />
            </div>
          </div>
        </label>

        <p class="text-read text-grey-600 text-center mt-6">
          Format de fichier supporté: PDF, DOC, XLS. Taille max: 5MB.
        </p>

        <button type="submit" class="btn lg w-100 flex-center mt-5 mb-6" :disabled="isSubmitting">
          <img src="/public/icons/icon-valider.svg" class="btn-icon" alt="" />
          <span>{{ isSubmitting ? 'Envoi en cours...' : 'Valider' }}</span>
        </button>

        <a @click.prevent="goToPreviousStep" class="cursor-pointer">
          <h5 class="flex-center gap-4 text-primary-color text-center">
            <img src="/public/icons/icon-back.svg" class="size-medium" alt="" /> 
            Étape précédente
          </h5>
        </a>
      </form>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showAlert } from '@/utils/swal'
import { getKycData, clearKycData, storedToFile } from '@/utils/kycStorage'
import axios from 'axios'
import { API_CONFIG, TokenStorage } from '@/services/apiConfig'

interface FormData {
  bulletin1: File | null
  bulletin2: File | null
  bulletin3: File | null
}

export default defineComponent({
  name: 'SignUpKyc2',
  props: {
    userName: {
      type: String,
      default: 'Utilisateur'
    },
    userEmail: {
      type: String,
      default: 'user@example.com'
    },
    userCompany: {
      type: String,
      default: 'Entreprise'
    },
    userPhoto: {
      type: String,
      default: ''
    }
  },
  setup() {
    const router = useRouter()
    const isSubmitting = ref(false)

    const formData = reactive<FormData>({
      bulletin1: null,
      bulletin2: null,
      bulletin3: null
    })

    const validateFile = (file: File): boolean => {
      const maxSize = 5 * 1024 * 1024 // 5MB
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ]

      // Vérifier l'extension du fichier
      const fileExtension = file.name.split('.').pop()?.toLowerCase()
      const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx']

      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        showAlert.error('Format de fichier non supporté. Veuillez utiliser PDF, DOC ou XLS.')
        return false
      }

      if (!allowedTypes.includes(file.type) && file.type !== '') {
        showAlert.error('Format de fichier non supporté. Veuillez utiliser PDF, DOC ou XLS.')
        return false
      }

      if (file.size > maxSize) {
        showAlert.error('La taille du fichier dépasse 5MB.')
        return false
      }

      return true
    }

    const handleBulletinUpload = (event: Event, bulletinNumber: number) => {
      const target = event.target as HTMLInputElement
      const file = target.files?.[0]

      if (file && validateFile(file)) {
        switch (bulletinNumber) {
          case 1:
            formData.bulletin1 = file
            break
          case 2:
            formData.bulletin2 = file
            break
          case 3:
            formData.bulletin3 = file
            break
        }
      } else {
        target.value = ''
      }
    }

    const handleSubmit = async () => {
      if (isSubmitting.value) return

      isSubmitting.value = true

      try {
        // Récupérer les données KYC stockées dans IndexedDB depuis la page précédente
        const kycData = await getKycData()
        if (!kycData) {
          await showAlert.error('Données KYC manquantes. Veuillez recommencer depuis l\'étape précédente.')
          router.push('/signup-kyc')
          return
        }

        // Construire un FormData pour envoyer les fichiers en multipart/form-data
        const fd = new window.FormData()
        fd.append('kyc', '0')
        fd.append('idtype', kycData.idtype)
        fd.append('idnumero', kycData.idnumero)

        // Reconstruire les File objects depuis les ArrayBuffers stockés dans IndexedDB
        if (kycData.selfie) {
          fd.append('selfie', storedToFile(kycData.selfie, 'selfie'))
        }
        if (kycData.idphoto) {
          fd.append('idphoto', storedToFile(kycData.idphoto, 'idphoto'))
        }
        if (kycData.idphoto2) {
          fd.append('idphoto2', storedToFile(kycData.idphoto2, 'idphoto2'))
        }

        // Ajouter les bulletins de salaire
        if (formData.bulletin1) {
          fd.append('bulletin1', formData.bulletin1)
        }
        if (formData.bulletin2) {
          fd.append('bulletin2', formData.bulletin2)
        }
        if (formData.bulletin3) {
          fd.append('bulletin3', formData.bulletin3)
        }

        const token = TokenStorage.getToken()
        const headers: Record<string, string> = {
          'Accept': 'application/json'
        }
        if (token) {
          headers['Authorization'] = `Bearer ${token}`
        }

        // Envoyer les données au backend en multipart/form-data
        const response = await axios.post(
          `${API_CONFIG.baseURL}/kyc/save`,
          fd,
          { headers }
        )

        if (response.data.status === 0) {
          // Nettoyer les données KYC de IndexedDB
          await clearKycData()

          await showAlert.success(response.data.message || 'KYC soumis avec succès')
          // Rediriger vers la page de confirmation
          router.push('/signup-back')
        } else {
          await showAlert.error(response.data.message || 'Erreur lors de la soumission du KYC')
        }
      } catch (error: any) {
        console.error('Erreur lors de la soumission du KYC:', error)
        await showAlert.error(error.response?.data?.message || 'Une erreur est survenue')
      } finally {
        isSubmitting.value = false
      }
    }

    const goToPreviousStep = () => {
      // Retourner à l'étape précédente (KYC)
      router.back()
      // Ou router.push('/signup-kyc')
    }

    return {
      formData,
      isSubmitting,
      handleBulletinUpload,
      handleSubmit,
      goToPreviousStep
    }
  }
})
</script>

<style scoped>
/* Styles spécifiques pour masquer les inputs file */
input[type="file"] {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
}

.custom-upload {
  cursor: pointer;
  transition: all 0.3s ease;
}

.custom-upload:hover {
  opacity: 0.9;
}

.custom-upload.active {
  border-color: var(--primary-color, #007bff);
}

.cursor-pointer {
  cursor: pointer;
}

.tag-state {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}

.rounded-50 {
  border-radius: 50px;
}

.rounded-100 {
  border-radius: 100%;
}

.size-2xl {
  width: 60px;
  height: 60px;
  object-fit: cover;
}

.text-grey-600 {
  color: #6c757d;
}
</style>

